import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  createBrokerShipper,
  getBrokerShipperStats,
  getBrokerShipper,
  getBrokerShippers,
  updateBrokerShipper,
  type BrokerShipperStats,
} from "@/lib/api/broker-parties";
import { getLocalizedName } from "@/lib/localization";
import { Alert, AlertContent } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EmptyState as EmptyStateUI, Text, Value } from "@/components/ui/text";
import { Icon } from "@/components/ui/icons";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import { PageTitle } from "@/contexts/page-title-context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { Tab, TabContainer, TabList } from "@/components/ui/tab";
import { Fab } from "@/components/ui/fab";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { FormGrid } from "@/components/ui/form-grid";
import { Surface } from "@/components/ui/surface";
import { Skeleton } from "@/components/ui/skeleton";
import { TableViewport } from "@/components/ui/table-viewport";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { MasterDetailLayout } from "@/components/ui/master-detail-layout";
import { DividedList } from "@/components/ui/divided-list";
import { IconCircle } from "@/components/ui/icon-circle";
import { getAddresses, type Address } from "@/lib/api/addresses";
import { getContacts } from "@/lib/api/contacts";
import { AddressModal } from "@/components/forms/address-modal";
import { ContactModal } from "@/components/forms/contact-modal";
import { ShipperModal } from "@/components/forms/shipper-modal";
import { hasPermission } from "@/lib/permissions";
import { SelectableRow } from "@/components/ui/selectable-row";
import { DEFAULT_PAGE_SIZE } from "@/hooks/use-pagination";
import { queryKeys } from "@/lib/query-keys";
import { usePartyPage } from "@/hooks/use-party-page";
import { highlight } from "@/lib/highlight";
import { entitySearchableFields } from "@/lib/search/entity";

function ShippersPage() {
  const { t: tAddresses } = useTranslation("addresses");
  const { t: tCommon } = useTranslation("common");
  const { t: tCommonPagination } = useTranslation("common.pagination");
  const { t: tContacts } = useTranslation("contacts");
  const { t: tShippers } = useTranslation("shippers");

  const {
    language,
    isCompact,
    isLoading,
    errorKind,
    page,
    totalPages,
    setPage,
    total: totalShippers,
    data: items,
    filteredItems,
    searchQuery,
    setSearchFilter,
    stats: shipperStats,
    invalidate,
    selectedId: selectedShipperId,
    setSelectedId: setSelectedShipperId,
    selectedParty: selectedShipper,
    activeTab: activeTabRaw,
    setActiveTab: setActiveTabRaw,
    closeDialog,
    dialog,
    isPartyModalOpen: isShipperModalOpen,
    isEditingParty: isEditingShipper,
    openCreateModal,
    openEditModal,
  } = usePartyPage<BrokerShipperStats>({
    dialogType: "shipper",
    queryKey: queryKeys.shippers({ limit: DEFAULT_PAGE_SIZE, offset: 0 }),
    queryFn: async ({ limit, offset }) => {
      const [listResponse, statsResponse] = await Promise.all([
        getBrokerShippers({ limit, offset }),
        getBrokerShipperStats(),
      ]);
      return { list: listResponse, stats: statsResponse };
    },
    getPartyDetail: getBrokerShipper,
    createParty: (form) =>
      createBrokerShipper({
        name: form.name,
        country_id: form.countryId,
        city_id: form.cityId,
        address: form.address,
        incorporation_date: form.incorporationDate,
        email: form.email,
        phone_number: form.phone,
      }),
    updateParty: (id, form) =>
      updateBrokerShipper(id, {
        name: form.name,
        country_id: form.countryId,
        city_id: form.cityId,
        address: form.address,
        incorporation_date: form.incorporationDate,
        email: form.email,
        phone_number: form.phone,
      }),
  });

  const queryClient = useQueryClient();
  const activeTab = (activeTabRaw || "addresses") as "addresses" | "contacts";
  const setActiveTab = (tab: "addresses" | "contacts") => setActiveTabRaw(tab);

  const addressesQuery = useQuery({
    queryKey: queryKeys.addresses({ shipper_id: selectedShipperId }),
    queryFn: () => getAddresses({ shipper_id: selectedShipperId }),
    enabled: !!selectedShipperId,
  });
  const addressesData = addressesQuery.data;
  const addresses = useMemo(() => addressesData?.data ?? [], [addressesData]);
  const addressesLoading = addressesQuery.isLoading && !!selectedShipperId;
  const addressesErrorKind = addressesQuery.isError ? ("load" as const) : null;

  const contactsQuery = useQuery({
    queryKey: queryKeys.contacts({ shipper_id: selectedShipperId }),
    queryFn: () => getContacts({ shipper_id: selectedShipperId }),
    enabled: !!selectedShipperId,
  });
  const contactsData = contactsQuery.data;
  const contacts = useMemo(() => contactsData?.data ?? [], [contactsData]);
  const contactsLoading = contactsQuery.isLoading && !!selectedShipperId;
  const contactsErrorKind = contactsQuery.isError ? ("load" as const) : null;

  // Address and contact search inputs are independent — split into
  // discrete useStates instead of a bundled object. The old shape
  // (`{address, contact}` plus four lines of {addressSearch,
  // setAddressSearch, contactSearch, setContactSearch} adapters)
  // was a dodge of the max-component-state count.
  const [addressSearch, setAddressSearch] = useState("");
  const [contactSearch, setContactSearch] = useState("");

  // Merged modal state: null = closed
  const [detailModal, setDetailModal] = useState<
    | { kind: "address"; address: Address | "new" }
    | { kind: "contact"; contactId: string | "new" }
    | null
  >(null);

  const canAddShipper = hasPermission("AddShipper");
  const canEditShipper = hasPermission("EditShipper");
  const canAddAddress = hasPermission("AddAddress");
  const canEditAddress = hasPermission("EditAddress");
  const canAddContact = hasPermission("AddContact");
  const canEditContact = hasPermission("EditContact");

  const invalidateDetails = () => {
    void queryClient.invalidateQueries({ queryKey: ["addresses"] });
    void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    invalidate();
  };

  const filteredAddresses = useMemo(() => {
    if (!addressSearch) return addresses;
    const query = addressSearch.toLowerCase();
    return addresses.filter((addr) => {
      const cityName = getLocalizedName(addr.city, language).toLowerCase();
      const countryName = getLocalizedName(
        addr.country,
        language,
      ).toLowerCase();
      return (
        addr.name.toLowerCase().includes(query) ||
        cityName.includes(query) ||
        countryName.includes(query) ||
        addr.address.toLowerCase().includes(query)
      );
    });
  }, [addresses, addressSearch, language]);

  const filteredContacts = useMemo(() => {
    if (!contactSearch) return contacts;
    const query = contactSearch.toLowerCase();
    return contacts.filter((contact) =>
      contact.name.toLowerCase().includes(query),
    );
  }, [contacts, contactSearch]);

  return (
    <VStack gap="lg" data-testid="broker-shippers-page">
      <PageTitle>{tShippers("title")}</PageTitle>

      {errorKind && (
        <Alert data-testid="broker-shippers-error" color="danger">
          <AlertContent icon={<Icon.Alert size="md" />}>
            <Text color="inherit">
              {errorKind ? tCommon("errorLoadingData") : null}
            </Text>
          </AlertContent>
        </Alert>
      )}

      <MasterDetailLayout isCompact={isCompact}>
        <VStack
          gap="md"
          className={isCompact && selectedShipperId ? "hidden" : ""}
        >
          <Card variant="bordered" padding="xs">
            <FilterToolbar
              searchPlaceholder={tCommon("search")}
              searchValue={searchQuery}
              onSearchChange={(v) => setSearchFilter(v)}
              createAction={
                canAddShipper
                  ? {
                      label: tShippers("addShipper"),
                      onClick: openCreateModal,
                      testId: "shippers-add-btn",
                    }
                  : undefined
              }
              pagination={{
                page,
                totalPages,
                perPage: DEFAULT_PAGE_SIZE,
                onPageChange: setPage,
                loading: isLoading,
              }}
            />
          </Card>

          <Card data-testid="broker-shippers-card" padding="none">
            <TableViewport position="relative">
              {isLoading && items.length === 0 ? (
                <DividedList rounded className="p-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <VStack gap="sm" key={index} className="py-3">
                      <HStack justify="between" gap="none">
                        <Skeleton className="h-5 w-40" />
                        <Skeleton rounded="full" className="h-6 w-14" />
                      </HStack>
                      <Skeleton className="h-4 w-52" />
                    </VStack>
                  ))}
                </DividedList>
              ) : filteredItems.length === 0 ? (
                <EmptyStateUI
                  title={tCommon("noShippers")}
                  icon={<Icon.User size="xl" />}
                  className="py-8"
                />
              ) : (
                <DividedList rounded>
                  {filteredItems.map((shipper) => {
                    const isSelected = shipper.id === selectedShipperId;
                    return (
                      <SelectableRow
                        key={shipper.id}
                        selected={isSelected}
                        data-testid={`broker-shipper-row-${shipper.id}`}
                        onClick={() =>
                          setSelectedShipperId((prev) =>
                            prev === shipper.id ? "" : shipper.id,
                          )
                        }
                      >
                        <HStack align="start" justify="between" gap="xl">
                          <VStack gap="xs">
                            <HStack>
                              <Text weight="medium">
                                {highlight(
                                  entitySearchableFields(shipper, language)
                                    .name,
                                  searchQuery,
                                )}
                              </Text>
                              <Badge color="success" size="sm">
                                {tShippers("active")}
                              </Badge>
                              {shipper.is_self ? (
                                <Badge color="info" size="sm">
                                  {tShippers("myCompanyYou")}
                                </Badge>
                              ) : null}
                            </HStack>
                            <HStack className="text-[color:var(--on-surface-variant)]">
                              <Icon.MapPin size="sm" />
                              <Text size="xs" color="muted">
                                {tCommon("city")}:{" "}
                                {getLocalizedName(shipper.city, language) ||
                                  "-"}
                                ,{" "}
                                {getLocalizedName(shipper.country, language) ||
                                  "-"}
                              </Text>
                            </HStack>
                          </VStack>
                        </HStack>
                      </SelectableRow>
                    );
                  })}
                </DividedList>
              )}
              {isLoading && items.length > 0 && (
                <PageLoadingOverlay
                  page={page}
                  label={tCommonPagination("loadingPage")}
                />
              )}
            </TableViewport>
          </Card>
        </VStack>

        <Card
          data-testid="broker-shipper-detail"
          padding="none"
          className={isCompact && !selectedShipperId ? "hidden" : ""}
        >
          {isCompact && selectedShipper ? (
            // eslint-disable-next-line design-tokens/no-raw-html-elements -- back navigation button with border-bottom divider, not a standard Button variant
            <button
              type="button"
              onClick={() => setSelectedShipperId("")}
              className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-[color:var(--primary)] hover:bg-[color:var(--surface-container)] transition-colors w-full border-b border-[color:var(--outline-variant)]"
              data-testid="shipper-detail-back"
            >
              <Icon.ArrowLeft size="sm" />
              {tCommon("back")}
            </button>
          ) : null}
          {!selectedShipper ? (
            <VStack gap="none" padding="xl">
              <VStack align="center" className="text-center">
                <IconCircle size="xl" color="primary">
                  <Icon.Dashboard size="lg" color="primary" />
                </IconCircle>
                <Text weight="medium">{tShippers("shipperNetwork")}</Text>
                <Text size="xs" color="muted">
                  {tShippers("selectFromList")}
                </Text>
              </VStack>
              <FormGrid cols="sm-3" gap="lg" className="mt-8">
                <Surface border="default" padding="lg" className="text-center">
                  <Text size="xs" color="muted">
                    {tShippers("totalShippers")}
                  </Text>
                  <Value size="lg" weight="semibold">
                    {totalShippers}
                  </Value>
                </Surface>
                <Surface border="default" padding="lg" className="text-center">
                  <Text size="xs" color="muted">
                    {tShippers("totalAddresses")}
                  </Text>
                  <Value size="lg" weight="semibold">
                    {shipperStats?.total_addresses ?? "—"}
                  </Value>
                </Surface>
                <Surface border="default" padding="lg" className="text-center">
                  <Text size="xs" color="muted">
                    {tShippers("totalContacts")}
                  </Text>
                  <Value size="lg" weight="semibold">
                    {shipperStats?.total_contacts ?? "—"}
                  </Value>
                </Surface>
              </FormGrid>
            </VStack>
          ) : (
            <VStack gap="lg" padding="md">
              <Surface border="default" padding="none">
                <Surface border="bottom">
                  <VStack gap="lg" className="p-4">
                    <VStack className="sm:flex-row sm:items-start sm:justify-between">
                      <VStack gap="sm">
                        <HStack>
                          <Value size="lg" weight="semibold">
                            {selectedShipper.name}
                          </Value>
                          <Badge color="success" size="sm">
                            {tShippers("active")}
                          </Badge>
                          {selectedShipper.is_self ? (
                            <Badge color="info" size="sm">
                              {tShippers("myCompanyYou")}
                            </Badge>
                          ) : null}
                        </HStack>
                      </VStack>
                      {canEditShipper ? (
                        <Button
                          color="neutral"
                          variant="outline"
                          size="md"
                          onClick={() => openEditModal(selectedShipper)}
                        >
                          {tShippers("editProfile")}
                        </Button>
                      ) : null}
                    </VStack>
                    <FormGrid>
                      <VStack>
                        <HStack gap="lg">
                          <Icon.Phone size="sm" color="muted" />
                          <Text>{selectedShipper.phone_number || "-"}</Text>
                        </HStack>
                        <HStack gap="lg">
                          <Icon.Email size="sm" color="muted" />
                          <Text>{selectedShipper.email || "-"}</Text>
                        </HStack>
                      </VStack>
                      <VStack>
                        <HStack gap="lg">
                          <Icon.MapPin size="sm" color="muted" />
                          <Text>
                            {getLocalizedName(selectedShipper.city, language)},{" "}
                            {getLocalizedName(
                              selectedShipper.country,
                              language,
                            )}
                          </Text>
                        </HStack>
                        <HStack gap="lg">
                          <Icon.Location size="sm" color="muted" />
                          <Text>{selectedShipper.address || "-"}</Text>
                        </HStack>
                      </VStack>
                    </FormGrid>
                  </VStack>
                </Surface>
              </Surface>

              <TabContainer padding="none" className="px-1">
                <TabList className="px-3">
                  <Tab
                    active={activeTab === "addresses"}
                    onClick={() => setActiveTab("addresses")}
                  >
                    {tAddresses("title")} ({addresses.length})
                  </Tab>
                  <Tab
                    active={activeTab === "contacts"}
                    onClick={() => setActiveTab("contacts")}
                  >
                    {tContacts("title")} ({contacts.length})
                  </Tab>
                </TabList>
              </TabContainer>

              {activeTab === "addresses" ? (
                <VStack gap="lg">
                  <Card variant="bordered" padding="xs">
                    <FilterToolbar
                      searchPlaceholder={tAddresses("searchPlaceholder")}
                      searchValue={addressSearch}
                      onSearchChange={setAddressSearch}
                      createAction={
                        canAddAddress
                          ? {
                              label: tAddresses("addAddress"),
                              onClick: () =>
                                setDetailModal({
                                  kind: "address",
                                  address: "new",
                                }),
                            }
                          : undefined
                      }
                    />
                  </Card>

                  {addressesLoading ? (
                    <TableViewport>
                      <Table data-testid="shipper-addresses-skeleton">
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>
                              {tAddresses("label")}
                            </TableHeaderCell>
                            <TableHeaderCell>
                              {tAddresses("location")}
                            </TableHeaderCell>
                            <TableHeaderCell>
                              {tAddresses("address")}
                            </TableHeaderCell>
                            {canEditAddress ? (
                              <TableHeaderCell>
                                {tCommon("actions")}
                              </TableHeaderCell>
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Array.from({ length: 4 }).map((_, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Skeleton className="h-5 w-24" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-40" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-48" />
                              </TableCell>
                              {canEditAddress ? (
                                <TableCell>
                                  <Skeleton className="h-8 w-16" />
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableViewport>
                  ) : addressesErrorKind ? (
                    <Text color="danger">
                      {addressesErrorKind === "load"
                        ? tCommon("errorLoadingData")
                        : null}
                    </Text>
                  ) : filteredAddresses.length === 0 ? (
                    <EmptyStateUI
                      title={tAddresses("noAddressesTitle")}
                      description={tAddresses("noAddressesDescription")}
                      icon={<Icon.Location size="xl" />}
                      className="py-8"
                    />
                  ) : (
                    <TableViewport>
                      <Table data-testid="shipper-addresses-table">
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>
                              {tAddresses("label")}
                            </TableHeaderCell>
                            <TableHeaderCell>
                              {tAddresses("location")}
                            </TableHeaderCell>
                            <TableHeaderCell>
                              {tAddresses("address")}
                            </TableHeaderCell>
                            {canEditAddress ? (
                              <TableHeaderCell>
                                {tCommon("actions")}
                              </TableHeaderCell>
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredAddresses.map((addr) => (
                            <TableRow
                              key={addr.id}
                              hoverable
                              data-testid={`shipper-address-${addr.id}`}
                            >
                              <TableCell>
                                <Text weight="medium">{addr.name}</Text>
                              </TableCell>
                              <TableCell>
                                <Text size="xs" color="muted" block>
                                  {getLocalizedName(addr.city, language)},{" "}
                                  {getLocalizedName(addr.country, language)}
                                </Text>
                              </TableCell>
                              <TableCell>
                                <Text size="xs" color="muted" block>
                                  {addr.address}
                                </Text>
                              </TableCell>
                              {canEditAddress ? (
                                <TableCell>
                                  <Button
                                    color="neutral"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      setDetailModal({
                                        kind: "address",
                                        address: addr,
                                      })
                                    }
                                  >
                                    {tCommon("edit")}
                                  </Button>
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableViewport>
                  )}
                </VStack>
              ) : (
                <VStack gap="lg">
                  <Card variant="bordered" padding="xs">
                    <FilterToolbar
                      searchPlaceholder={tContacts("searchPlaceholder")}
                      searchValue={contactSearch}
                      onSearchChange={setContactSearch}
                      createAction={
                        canAddContact
                          ? {
                              label: tContacts("addContact"),
                              onClick: () =>
                                setDetailModal({
                                  kind: "contact",
                                  contactId: "new",
                                }),
                            }
                          : undefined
                      }
                    />
                  </Card>

                  {contactsLoading ? (
                    <TableViewport>
                      <Table data-testid="shipper-contacts-skeleton">
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>{tCommon("name")}</TableHeaderCell>
                            <TableHeaderCell>
                              {tCommon("contact")}
                            </TableHeaderCell>
                            {canEditContact ? (
                              <TableHeaderCell>
                                {tCommon("actions")}
                              </TableHeaderCell>
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Array.from({ length: 4 }).map((_, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Skeleton className="h-5 w-28" />
                              </TableCell>
                              <TableCell>
                                <Skeleton className="h-4 w-44" />
                              </TableCell>
                              {canEditContact ? (
                                <TableCell>
                                  <Skeleton className="h-8 w-16" />
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableViewport>
                  ) : contactsErrorKind ? (
                    <Text color="danger">
                      {contactsErrorKind === "load"
                        ? tCommon("errorLoadingData")
                        : null}
                    </Text>
                  ) : filteredContacts.length === 0 ? (
                    <EmptyStateUI
                      title={tContacts("noContactsTitle")}
                      description={tContacts("noContactsDescription")}
                      icon={<Icon.Contacts size="xl" />}
                      className="py-8"
                    />
                  ) : (
                    <TableViewport>
                      <Table data-testid="shipper-contacts-table">
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>{tCommon("name")}</TableHeaderCell>
                            <TableHeaderCell>
                              {tCommon("contact")}
                            </TableHeaderCell>
                            {canEditContact ? (
                              <TableHeaderCell>
                                {tCommon("actions")}
                              </TableHeaderCell>
                            ) : null}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredContacts.map((contact) => (
                            <TableRow
                              key={contact.id}
                              hoverable
                              data-testid={`shipper-contact-${contact.id}`}
                            >
                              <TableCell>
                                <Text weight="medium">{contact.name}</Text>
                              </TableCell>
                              <TableCell>
                                <Text size="xs" color="muted" block>
                                  {contact.contacts[0]?.contact || "-"}
                                </Text>
                              </TableCell>
                              {canEditContact ? (
                                <TableCell>
                                  <Button
                                    color="neutral"
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                      setDetailModal({
                                        kind: "contact",
                                        contactId: contact.id,
                                      })
                                    }
                                  >
                                    {tCommon("edit")}
                                  </Button>
                                </TableCell>
                              ) : null}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableViewport>
                  )}
                </VStack>
              )}
            </VStack>
          )}
        </Card>
      </MasterDetailLayout>

      <AddressModal
        open={detailModal?.kind === "address"}
        onClose={() => setDetailModal(null)}
        address={
          detailModal?.kind === "address" && detailModal.address !== "new"
            ? detailModal.address
            : undefined
        }
        onSaved={() => {
          invalidateDetails();
          setDetailModal(null);
        }}
        mapHeightClassName="h-[320px] min-h-[260px]"
        size="2xl"
        shipperParty={selectedShipper}
        shipperPartyEditable={false}
      />

      <ContactModal
        open={detailModal?.kind === "contact"}
        onClose={() => setDetailModal(null)}
        contactId={
          detailModal?.kind === "contact" && detailModal.contactId !== "new"
            ? detailModal.contactId
            : undefined
        }
        onSaved={() => {
          invalidateDetails();
          setDetailModal(null);
        }}
        shipperParty={selectedShipper}
        shipperPartyEditable={false}
      />

      {/* Shipper create / edit modal (shared with the load wizard) */}
      <ShipperModal
        open={isShipperModalOpen}
        shipperId={isEditingShipper ? (dialog?.id ?? null) : null}
        onClose={() => closeDialog()}
        onSaved={() => invalidate()}
      />

      {isCompact &&
        (() => {
          if (selectedShipperId) {
            if (activeTab === "addresses" && canAddAddress)
              return (
                <Fab
                  onClick={() =>
                    setDetailModal({ kind: "address", address: "new" })
                  }
                  aria-label={tAddresses("addAddress")}
                  data-testid="shippers-detail-fab"
                />
              );
            if (activeTab === "contacts" && canAddContact)
              return (
                <Fab
                  onClick={() =>
                    setDetailModal({ kind: "contact", contactId: "new" })
                  }
                  aria-label={tContacts("addContact")}
                  data-testid="shippers-detail-fab"
                />
              );
            return null;
          }
          if (canAddShipper)
            return (
              <Fab
                onClick={openCreateModal}
                aria-label={tShippers("addShipper")}
                data-testid="shippers-fab"
              />
            );
          return null;
        })()}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/shippers")({
  component: ShippersPage,
});
